import { useEffect, useId, useRef, useState } from "react";
import { Download, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmailService } from "@/lib/email-service";
import { useToast } from "@/hooks/use-toast";
import { DEFAULT_REPORT_FEEDBACK, generateFacadeReportPDF, type FacadeReportData } from "@/lib/facade-report";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import { facadeReportDefaultsSchema } from "@shared/facade-report-defaults";

type Props = Omit<FacadeReportData, "feedback">;

const reportFilename = (projectName: string) =>
  `Fassadencheck_${(projectName || "Bericht").replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, "_")}.pdf`;

export default function FacadeReportExport(props: Props) {
  const { user } = useAuth();
  return <PersonalFacadeReportExport key={user?.id ?? "anonymous"} {...props} userId={user?.id} />;
}

function PersonalFacadeReportExport({ userId, ...props }: Props & { userId?: number }) {
  const id = useId();
  const { toast } = useToast();
  const {
    feedback, setFeedback, loadingDefaults, savingDefaults, defaultsError,
    defaultsLoaded, savedFeedback, setLoadAttempt, saveDefaults,
  } = usePersonalReportDefaults(userId);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState(`Guten Tag,\n\nanbei erhalten Sie den Fassadencheck-Bericht zum Projekt „${props.projectName}“ mit Fotos und Schadensbeschreibungen. Bitte geben Sie uns eine Rückmeldung zu den offenen Abklärungen. Der Bericht ist noch kein kalkuliertes Angebot.\n\nMit freundlichen Grüßen`);
  const [confirmation, setConfirmation] = useState<{ report: FacadeReportData; to: string; message: string } | null>(null);
  const [sendError, setSendError] = useState("");
  const sending = useRef(false);
  const recipientInput = useRef<HTMLInputElement>(null);
  const messageInput = useRef<HTMLTextAreaElement>(null);
  const photos = props.photos.filter((photo) => !excluded.has(photo.id));
  const hasContent = photos.length > 0 || !!props.description?.trim();
  const filename = reportFilename(props.projectName);

  const prepareEmail = () => {
    if (busy || loadingDefaults || !hasContent || !recipient.trim() || !message.trim()) return;
    if (!recipientInput.current?.reportValidity() || !messageInput.current?.reportValidity()) return;
    setSendError("");
    setConfirmation({
      report: {
        projectName: props.projectName, customerName: props.customerName, address: props.address,
        description: props.description, feedback, photos: photos.map((photo) => ({ ...photo })),
      },
      to: recipient.trim(), message: message.trim(),
    });
  };

  const send = async () => {
    if (!confirmation || sending.current) return;
    sending.current = true;
    setBusy(true);
    setSendError("");
    try {
      const service = new EmailService();
      if (!service.isConfigured()) {
        throw new Error("Der direkte E-Mail-Versand ist nicht eingerichtet. Bitte den Bericht als PDF herunterladen und manuell anhängen.");
      }
      const pdf = await generateFacadeReportPDF(confirmation.report);
      await service.sendFacadeReportEmail({
        to: confirmation.to, subject: `Fassadencheck-Bericht: ${confirmation.report.projectName}`,
        message: confirmation.message, customerName: confirmation.report.customerName,
        projectName: confirmation.report.projectName, attachments: [pdf], attachmentName: reportFilename(confirmation.report.projectName),
      });
      toast({ title: "Fassadencheck-Bericht gesendet", description: `Der Bericht wurde an ${confirmation.to} gesendet.` });
      setConfirmation(null);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Der Bericht konnte nicht gesendet werden. Bitte erneut versuchen oder das PDF manuell versenden.");
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    setError("");
    let url: string | undefined;
    try {
      const blob = await generateFacadeReportPDF({ ...props, feedback, photos });
      url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: "Fassadencheck-Bericht erstellt", description: "Der Bericht enthält die ausgewählten Fotos und Beschreibungen – ohne Preise oder Kalkulation." });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Der Bericht konnte nicht erstellt werden.");
    } finally {
      if (url) window.setTimeout(() => URL.revokeObjectURL(url!), 1000);
      setBusy(false);
    }
  };

  return <Card>
    <CardHeader>
      <CardTitle className="text-base text-bauwerk-teal">Fassadencheck-Bericht ohne Kalkulation</CardTitle>
      <p className="text-sm text-slate-600">Fotos und Schadensbeschreibungen als PDF für die Abklärung vor dem Angebot. Keine Positionen, Preise oder Summen erforderlich. Das bestehende Angebot bleibt unverändert.</p>
    </CardHeader>
    <CardContent className="space-y-4">
      <fieldset disabled={busy} className="space-y-2">
        <legend className="mb-2 text-sm font-medium">Fotos für den Bericht ({photos.length} von {props.photos.length})</legend>
        {props.photos.map((photo, index) => <label key={photo.id} className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1 accent-[var(--bauwerk-teal)]" checked={!excluded.has(photo.id)}
            onChange={(event) => setExcluded((current) => {
              const next = new Set(current);
              if (event.target.checked) next.delete(photo.id); else next.add(photo.id);
              return next;
            })} />
          <span className="min-w-0 break-words">Foto {index + 1}: {photo.title}
            {!photo.description.trim() && <span className="ml-2 text-xs text-amber-700">Noch ohne Schadensbeschreibung</span>}
          </span>
        </label>)}
      </fieldset>
      <div className="space-y-2">
        <label htmlFor={`${id}-feedback`} className="text-sm font-medium">Bitte um Rückmeldung / offene Abklärungen</label>
        <Textarea id={`${id}-feedback`} rows={4} maxLength={10000} value={feedback} disabled={busy || loadingDefaults || savingDefaults} onChange={(event) => setFeedback(event.target.value)} />
        <p className="text-xs text-slate-500">Änderungen gelten nur für diesen Bericht. Erst „Als persönliche Vorgabe speichern“ übernimmt den Text für weitere Berichte Ihres Benutzerkontos. Angebots- und Rechnungsvorgaben bleiben unverändert.</p>
        {loadingDefaults && <p role="status" className="text-sm text-slate-600">Persönliche Vorgabe wird geladen…</p>}
        <Button type="button" variant="outline" disabled={!userId || !defaultsLoaded || loadingDefaults || savingDefaults || busy || feedback === savedFeedback}
          onClick={() => void saveDefaults()}>
          {savingDefaults ? "Vorgabe wird gespeichert…" : "Als persönliche Vorgabe speichern"}
        </Button>
        {!loadingDefaults && savedFeedback !== null && feedback === savedFeedback && <p role="status" className="text-sm text-slate-600">Persönliche Vorgabe übernommen.</p>}
        {defaultsError && <p role="alert" className="text-sm text-red-700">{defaultsError}</p>}
        {defaultsError && !defaultsLoaded && <Button type="button" variant="outline" disabled={loadingDefaults || busy}
          onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Vorgabe erneut laden</Button>}
      </div>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <Button type="button" onClick={() => void download()} disabled={busy || loadingDefaults || !hasContent}
        className="w-full sm:w-auto bg-bauwerk-teal hover:bg-bauwerk-teal/90">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
        {busy ? "Bericht wird erstellt…" : "Fassadencheck-Bericht als PDF"}
      </Button>
      <p className="text-xs text-slate-500">Das heruntergeladene PDF können Sie öffnen und ausdrucken. Lange Beschreibungen werden vollständig auf Folgeseiten fortgesetzt.</p>
      <div className="space-y-3 border-t pt-4">
        <div className="space-y-2">
          <label htmlFor={`${id}-recipient`} className="text-sm font-medium">Empfänger-E-Mail</label>
          <Input id={`${id}-recipient`} ref={recipientInput} type="email" inputMode="email" form="" required value={recipient} disabled={busy}
            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); prepareEmail(); } }}
            onChange={(event) => setRecipient(event.target.value)} placeholder="name@beispiel.at" />
        </div>
        <div className="space-y-2">
          <label htmlFor={`${id}-message`} className="text-sm font-medium">Begleittext der E-Mail</label>
          <Textarea id={`${id}-message`} ref={messageInput} form="" required rows={6} value={message} disabled={busy}
            onChange={(event) => setMessage(event.target.value)} />
        </div>
        <Button type="button" onClick={prepareEmail} variant="outline" disabled={busy || loadingDefaults || !hasContent || !recipient.trim() || !message.trim()} className="w-full sm:w-auto">
          <Mail className="mr-2 h-4 w-4" /> E-Mail-Versand prüfen
        </Button>
        <p className="text-xs text-slate-500">Vor dem Versand bestätigen Sie Empfänger, Begleittext und Fotoauswahl. Die ausgewählten Fotos sind im PDF enthalten, nicht als separate Bildanhänge.</p>
      </div>
      <Dialog open={!!confirmation} onOpenChange={(open) => { if (!open && !sending.current) setConfirmation(null); }}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto" onEscapeKeyDown={(event) => { if (busy) event.preventDefault(); }}
          onPointerDownOutside={(event) => { if (busy) event.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle>Fassadencheck-Bericht senden?</DialogTitle>
            <DialogDescription>Bitte prüfen Sie die Angaben. Es wird ausschließlich der Bericht als PDF versendet – kein Angebot erstellt oder geändert.</DialogDescription>
          </DialogHeader>
          {confirmation && <div className="min-w-0 space-y-3 text-sm">
            <p className="break-words"><strong>An:</strong> {confirmation.to}</p>
            <p className="break-words"><strong>Betreff:</strong> Fassadencheck-Bericht: {confirmation.report.projectName}</p>
            <div><strong>Begleittext:</strong><p className="whitespace-pre-wrap break-words mt-1">{confirmation.message}</p></div>
            <div><strong>Fotos im PDF ({confirmation.report.photos.length}):</strong>
              <ul className="list-disc pl-5">{confirmation.report.photos.map((photo) => <li key={photo.id} className="break-words">{photo.title}</li>)}</ul>
              {!confirmation.report.photos.length && <p>Keine Fotos – nur Projektbeschreibung.</p>}
            </div>
            <div><strong>Rückmeldungsbitte im PDF:</strong><p className="whitespace-pre-wrap break-words">{confirmation.report.feedback || "Keine"}</p></div>
          </div>}
          {sendError && <p role="alert" className="text-sm text-red-700">{sendError}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={() => setConfirmation(null)}>Zurück / bearbeiten</Button>
            <Button type="button" disabled={busy} onClick={() => void send()}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {busy ? "Bericht wird gesendet…" : "Jetzt verbindlich senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent>
  </Card>;
}
function usePersonalReportDefaults(userId?: number) {
  const { toast } = useToast();
  const [feedback, setFeedback] = useState(DEFAULT_REPORT_FEEDBACK);
  const [loadingDefaults, setLoadingDefaults] = useState(!!userId);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [defaultsError, setDefaultsError] = useState("");
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [savedFeedback, setSavedFeedback] = useState<string | null>(null);
  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    setLoadingDefaults(true);
    setDefaultsError("");
    void (async () => {
      try {
        const response = await apiRequest("GET", "/api/facade-report-defaults", undefined, controller.signal);
        const data = await response.json();
        const loaded = data === null ? null : facadeReportDefaultsSchema.parse(data);
        if (controller.signal.aborted) return;
        if (loaded) setFeedback(loaded.feedback);
        setSavedFeedback(loaded?.feedback ?? null);
        setDefaultsLoaded(true);
      } catch {
        if (!controller.signal.aborted) setDefaultsError("Ihre persönliche Berichtsvorgabe konnte nicht geladen werden. Der aktuelle Text kann weiterhin für diesen Bericht verwendet werden.");
      } finally {
        if (!controller.signal.aborted) setLoadingDefaults(false);
      }
    })();
    return () => controller.abort();
  }, [userId, loadAttempt]);

  const saveDefaults = async () => {
    setSavingDefaults(true);
    setDefaultsError("");
    try {
      await apiRequest("PUT", "/api/facade-report-defaults", { feedback });
      setSavedFeedback(feedback);
      toast({ title: "Persönliche Berichtsvorgabe gespeichert", description: "Der Rückmeldungstext wird bei weiteren Fassadencheck-Berichten geladen." });
    } catch {
      setDefaultsError("Ihre persönliche Berichtsvorgabe konnte nicht gespeichert werden. Ihr Text bleibt für diesen Bericht erhalten. Bitte versuchen Sie es erneut.");
    } finally {
      setSavingDefaults(false);
    }
  };
  return {
    feedback, setFeedback, loadingDefaults, savingDefaults, defaultsError,
    defaultsLoaded, savedFeedback, setLoadAttempt, saveDefaults,
  };
}
